import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  DollarSign, ArrowLeft, Loader2, Play, Calendar, Building2,
  ChevronDown, CheckCircle2, AlertCircle, CreditCard
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentProposalCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [executing, setExecuting] = useState(false);
  
  const [companyCodes, setCompanyCodes] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  const [formData, setFormData] = useState({
    company_code: '',
    payment_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // +7 days
    due_date_to: new Date().toISOString().split('T')[0],
    vendor_from: '',
    vendor_to: '',
    include_overdue: true,
    take_cash_discount: true,
    payment_method: 'T',
    created_by: 'ADMIN'
  });
  
  const [proposal, setProposal] = useState(null);
  const [paymentRun, setPaymentRun] = useState(null);

  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    try {
      const [ccRes, pmRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/company/company-codes`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/ap/payment-methods`)
      ]);
      setCompanyCodes(ccRes.data || []);
      setPaymentMethods(pmRes.data || []);
      
      // Set default company code
      if (ccRes.data && ccRes.data.length > 0) {
        setFormData(prev => ({ ...prev, company_code: ccRes.data[0].company_code_id }));
      }
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  const handleSyncOpenItems = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(`${BACKEND_URL}/api/ap/open-items/sync`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to sync open items');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateProposal = async () => {
    if (!formData.company_code) {
      toast.error('Please select a company code');
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(`${BACKEND_URL}/api/ap/proposals`, formData);
      setProposal(response.data);
      
      if (response.data.items.length === 0) {
        toast.error('No items found matching the criteria');
      } else {
        toast.success(`Proposal created with ${response.data.items.length} items`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create proposal');
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteRun = async () => {
    if (!proposal) return;
    
    try {
      setExecuting(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/ap/proposals/${proposal.proposal_id}/execute?executed_by=ADMIN`
      );
      setPaymentRun(response.data);
      toast.success(`Payment run completed! ${response.data.payments_created} payments created`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute payment run');
    } finally {
      setExecuting(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="payment-proposal-create">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/ap/payments')}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <DollarSign className="w-7 h-7 text-emerald-600" />
                Payment Run (F110)
              </h1>
              <p className="text-sm text-gray-500">Automatic vendor payment processing</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleSyncOpenItems} disabled={syncing}>
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
            Sync Open Items
          </Button>
        </div>

        {/* Step 1: Selection Criteria */}
        {!proposal && !paymentRun && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
              Selection Criteria
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <Label>Company Code *</Label>
                <div className="relative mt-1">
                  <select
                    value={formData.company_code}
                    onChange={(e) => setFormData({...formData, company_code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm appearance-none bg-white pr-10"
                  >
                    <option value="">Select...</option>
                    {companyCodes.map(cc => (
                      <option key={cc.company_code_id} value={cc.company_code_id}>
                        {cc.company_code_id} - {cc.company_code_name || cc.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>
              
              <div>
                <Label>Payment Date *</Label>
                <Input
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Due Date To *</Label>
                <Input
                  type="date"
                  value={formData.due_date_to}
                  onChange={(e) => setFormData({...formData, due_date_to: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Payment Method</Label>
                <div className="relative mt-1">
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm appearance-none bg-white pr-10"
                  >
                    {paymentMethods.map(pm => (
                      <option key={pm.value} value={pm.value}>{pm.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>
              
              <div>
                <Label>Vendor From</Label>
                <Input
                  value={formData.vendor_from}
                  onChange={(e) => setFormData({...formData, vendor_from: e.target.value})}
                  placeholder="Optional"
                />
              </div>
              
              <div>
                <Label>Vendor To</Label>
                <Input
                  value={formData.vendor_to}
                  onChange={(e) => setFormData({...formData, vendor_to: e.target.value})}
                  placeholder="Optional"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="include_overdue"
                  checked={formData.include_overdue}
                  onCheckedChange={(checked) => setFormData({...formData, include_overdue: checked})}
                />
                <Label htmlFor="include_overdue">Include Overdue Items</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="take_cash_discount"
                  checked={formData.take_cash_discount}
                  onCheckedChange={(checked) => setFormData({...formData, take_cash_discount: checked})}
                />
                <Label htmlFor="take_cash_discount">Take Cash Discount</Label>
              </div>
            </div>
            
            <Button onClick={handleCreateProposal} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating Proposal...</>
              ) : (
                <><Play className="w-4 h-4 mr-2" />Create Proposal</>
              )}
            </Button>
          </div>
        )}

        {/* Step 2: Proposal Review */}
        {proposal && !paymentRun && (
          <div className="space-y-6">
            {/* Proposal Summary */}
            <div className="bg-gradient-to-r from-emerald-50 to-orange-50 rounded-lg border border-emerald-200 p-6">
              <h2 className="text-lg font-semibold text-emerald-900 mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                Proposal Review: {proposal.proposal_id}
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Items to Pay</p>
                  <p className="text-2xl font-bold text-emerald-600">{proposal.total_items}</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Total Invoice Amount</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(proposal.total_amount)}</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Cash Discount</p>
                  <p className="text-2xl font-bold text-green-600">-{formatCurrency(proposal.total_discount)}</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Net Payment</p>
                  <p className="text-2xl font-bold text-emerald-700">{formatCurrency(proposal.net_payment)}</p>
                </div>
              </div>
            </div>

            {/* Proposal Items */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-gray-900">Items to be Paid</h3>
              </div>
              
              {proposal.items.length === 0 ? (
                <div className="p-8 text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                  <p className="text-gray-600">No items found matching the criteria</p>
                  <p className="text-sm text-gray-500 mt-2">Try adjusting the due date or vendor range</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Open Amount</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Discount</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Payment Amt</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Days Overdue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {proposal.items.map((item, index) => (
                        <tr key={`${item.invoice_document || 'inv'}-${item.vendor_id || 'v'}-${index}`} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">{item.vendor_id}</p>
                            <p className="text-xs text-gray-500 truncate max-w-[150px]">{item.vendor_name}</p>
                          </td>
                          <td className="px-4 py-3 text-emerald-600 font-medium">{item.invoice_document}</td>
                          <td className="px-4 py-3 text-gray-600">{item.invoice_date}</td>
                          <td className="px-4 py-3 text-gray-600">{item.due_date}</td>
                          <td className="px-4 py-3 text-right font-medium">{formatCurrency(item.open_amount)}</td>
                          <td className="px-4 py-3 text-right text-green-600">
                            {item.discount_amount > 0 ? `-${formatCurrency(item.discount_amount)}` : '-'}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-emerald-700">{formatCurrency(item.payment_amount)}</td>
                          <td className="px-4 py-3 text-center">
                            {item.days_overdue > 0 ? (
                              <span className="text-red-600 font-medium">{item.days_overdue}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center">
              <Button variant="outline" onClick={() => setProposal(null)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Selection
              </Button>
              
              {proposal.items.length > 0 && (
                <Button onClick={handleExecuteRun} disabled={executing} className="bg-emerald-600 hover:bg-emerald-700">
                  {executing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Executing...</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-2" />Execute Payment Run</>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Payment Run Results */}
        {paymentRun && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 p-6">
              <h2 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
                Payment Run Completed: {paymentRun.run_id}
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Vendors Processed</p>
                  <p className="text-2xl font-bold text-green-600">{paymentRun.vendors_processed}</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Invoices Processed</p>
                  <p className="text-2xl font-bold text-gray-900">{paymentRun.invoices_processed}</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Payments Created</p>
                  <p className="text-2xl font-bold text-emerald-600">{paymentRun.payments_created}</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600">Total Payment</p>
                  <p className="text-2xl font-bold text-emerald-700">{formatCurrency(paymentRun.total_payment_amount)}</p>
                </div>
              </div>
            </div>

            {/* Created Payments */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-gray-900">Created Payments</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment #</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Invoices</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Discount</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Payment</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paymentRun.payments.map((payment) => (
                      <tr key={payment.payment_number} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-emerald-600">{payment.payment_number}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{payment.vendor_id}</p>
                          <p className="text-xs text-gray-500">{payment.vendor_name}</p>
                        </td>
                        <td className="px-4 py-3 text-right">{payment.invoice_count}</td>
                        <td className="px-4 py-3 text-right font-medium">{formatCurrency(payment.total_amount)}</td>
                        <td className="px-4 py-3 text-right text-green-600">
                          {payment.discount_taken > 0 ? `-${formatCurrency(payment.discount_taken)}` : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-emerald-700">{formatCurrency(payment.net_amount)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">
                            {payment.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center">
              <Button variant="outline" onClick={() => { setProposal(null); setPaymentRun(null); }}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                New Payment Run
              </Button>
              
              <Button onClick={() => navigate('/ap/payments')} className="bg-emerald-600 hover:bg-emerald-700">
                View All Payments
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PaymentProposalCreate;
